import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';

interface ClickableCellProps {
  /** The href to navigate to */
  href: string;
  /** The cell content to display */
  children: ReactNode;
  /** The full text value for the title attribute */
  title?: string;
  /** Whether to stop propagation to row click handler */
  stopPropagation?: boolean;
  /** Additional content to render (e.g., badges, icons) */
  addon?: ReactNode;
  /** HTML link target attribute */
  target?: string;
}

/**
 * ClickableCell - Standardized clickable link cell for DataTable
 *
 * Features:
 * - Subtle hover interaction with underline
 * - Supports cmd+click to open in new tab
 * - Stops row click propagation by default
 * - Accessible with proper link semantics
 * - Dark mode support
 * - Enhanced tooltip to show full text when truncated
 *
 * Design principles:
 * - Text color matches table text (not jarring blue)
 * - Hover state provides clear affordance (underline + subtle color shift)
 * - No underline by default to reduce visual noise
 * - Tooltip shows full content for truncated text
 */
export const ClickableCell = ({
  href,
  children,
  title,
  stopPropagation = true,
  addon,
  target,
}: ClickableCellProps) => {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
  };

  const handleAuxClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
  };

  const linkElement = (
    <Link
      to={href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      className="truncate font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 hover:underline transition-colors"
      onClick={handleClick}
      onAuxClick={handleAuxClick}
    >
      {children}
    </Link>
  );

  return (
    <div className="flex items-center gap-2 min-w-0">
      {title ? (
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>{linkElement}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-md">
            <p className="text-xs break-words">{title}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        linkElement
      )}
      {addon}
    </div>
  );
};
